/**
 * login-page.tsx — Email/password login (iltizam Phase 6).
 *
 * iltizam-card on top of the warm-cream gradient shell. Pill button
 * for sign-in, ink background. Same form behavior as the legacy card.
 */

import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ErrorBanner } from '@/components/error-banner';
import { authApi } from '@/lib/api/auth';
import { useAuthStore } from '@/lib/auth-store';

const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
});
type LoginFormValues = z.infer<typeof loginSchema>;
interface LocationState {
  from?: string;
}

export function LoginPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useAuthStore((s) => s.setSession);
  const [submitError, setSubmitError] = useState<unknown>(null);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const mutation = useMutation({
    mutationFn: authApi.login,
    onSuccess: (data) => {
      setSession({
        user: data.user,
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        expiresInSeconds: data.expiresIn,
      });
      const from = (location.state as LocationState | null)?.from;
      navigate(from && from.startsWith('/app') ? from : '/app', { replace: true });
    },
    onError: (error) => setSubmitError(error),
  });

  const onSubmit = form.handleSubmit((values) => {
    setSubmitError(null);
    mutation.mutate(values);
  });

  return (
    <div className="iltizam-card p-8">
      <h1 className="m-0 text-[28px] font-extrabold leading-tight text-iltizam-ink">
        {t('auth.login_title')}
      </h1>
      <p className="mt-1.5 text-[13px] text-iltizam-muted">{t('auth.login_subtitle')}</p>

      <form onSubmit={onSubmit} className="mt-6 space-y-4" noValidate>
        <ErrorBanner error={submitError} />

        <div className="space-y-1.5">
          <Label htmlFor="email">{t('auth.email')}</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            dir="ltr"
            aria-invalid={!!form.formState.errors.email}
            {...form.register('email')}
          />
          {form.formState.errors.email ? (
            <p className="text-xs text-iltizam-red">{t('auth.email_invalid')}</p>
          ) : null}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="password">{t('auth.password')}</Label>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            dir="ltr"
            aria-invalid={!!form.formState.errors.password}
            {...form.register('password')}
          />
          {form.formState.errors.password ? (
            <p className="text-xs text-iltizam-red">{t('auth.password_min')}</p>
          ) : null}
        </div>

        <button
          type="submit"
          disabled={mutation.isPending}
          className="inline-flex w-full items-center justify-center gap-1.5 rounded-pill bg-iltizam-ink px-4 py-2.5 text-[13px] font-bold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-50"
        >
          {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          <span>{t('auth.sign_in')}</span>
        </button>

        <div className="text-center text-[12.5px] text-iltizam-muted">
          <Link to="/auth/forgot-password" className="hover:text-iltizam-ink hover:underline">
            {t('auth.forgot_password')}
          </Link>
        </div>
      </form>
    </div>
  );
}
